import { MeterAppointment } from "./meterAppointment"
import IParser from "@/types/IParser"
import ParseError from "@/types/ParseError"
import dayjs from "dayjs"
import {
  IMeterAppointment,
  MeterAppointmentStatus,
  IMeterAppointmentEvent,
  IMeterAppointmentComment,
  IMeterAppointmentSurvey,
} from "./meterAppointment.types"
import { safeDayjs } from "@/helpers/dateHelper"
import { SmAppointment } from "@/api/backofficeRest/generated/model"

export type IMeterAppointmentRaw = SmAppointment

// The query returns no id on appointments; the domain model keeps an `id`
// field (currently unused downstream) so we default it here.
const NO_ID = 0

const toSurvey = (
  survey: IMeterAppointmentRaw["survey"],
): IMeterAppointmentSurvey[] | undefined =>
  survey?.flatMap((entry) =>
    entry
      ? [
          {
            name: entry.name ?? "",
            groupName: entry.group_name ?? "",
            question: entry.question ?? "",
            answer: entry.answer ?? "",
            imageUrl: entry.image_url ?? "",
          },
        ]
      : [],
  )

const toEvents = (
  events: IMeterAppointmentRaw["events"],
): IMeterAppointmentEvent[] | undefined =>
  events?.flatMap((event) =>
    event
      ? [
          {
            type: event.type ?? "",
            status: event.status ?? "",
            action: event.action ?? "",
            data: event.data ?? "",
            createdAt: event.createdAt ?? "",
            updatedAt: event.updatedAt ?? "",
          },
        ]
      : [],
  )

const toComments = (
  comments: IMeterAppointmentRaw["comments"],
): IMeterAppointmentComment[] | undefined =>
  comments?.flatMap((comment) =>
    comment
      ? [
          {
            comment: comment.comment ?? "",
            createdAt: comment.createdAt ?? "",
            updatedAt: comment.updatedAt ?? "",
          },
        ]
      : [],
  )

const toHistory = (
  history: IMeterAppointmentRaw["history"],
): MeterAppointment[] | undefined =>
  history?.flatMap((entry) =>
    entry
      ? [
          new MeterAppointment(
            NO_ID,
            dayjs(entry.slot_start_date_time),
            dayjs(entry.slot_end_date_time),
            entry.mpan ?? "",
            entry.mprn ?? "",
            entry.engineer_name ?? "",
            entry.engineer_email ?? "",
            entry.appointment_id ?? "",
            entry.electricity_meter_serial_number ?? "",
            entry.gas_meter_serial_number ?? "",
            entry.display_job_number ?? "",
            entry.aes_job_type ?? "",
            (entry.aes_status ?? null) as MeterAppointmentStatus | null,
            entry.appointment_type ?? "",
            entry.meter_type ?? "",
            entry.installation_type ?? "",
            entry.timeslot ?? "",
            dayjs(entry.created_at),
            safeDayjs(entry.cancellation_date_time),
            safeDayjs(entry.installed_datetime),
            safeDayjs(entry.failed_datetime),
            undefined, // survey is not fetched on history items
          ),
        ]
      : [],
  )

export const MeterAppointmentsParser: IParser<
  IMeterAppointment[],
  IMeterAppointmentRaw[]
> = {
  toDomain: (data: IMeterAppointmentRaw[]) => {
    try {
      if (!data.length) return []
      return data.map(
        (item) =>
          new MeterAppointment(
            NO_ID,
            dayjs(item.slot_start_date_time),
            dayjs(item.slot_end_date_time),
            item.mpan ?? "",
            item.mprn ?? "",
            item.engineer_name ?? "",
            item.engineer_email ?? "",
            item.appointment_id ?? "",
            item.electricity_meter_serial_number ?? "",
            item.gas_meter_serial_number ?? "",
            item.display_job_number ?? "",
            item.aes_job_type ?? "",
            (item.aes_status ?? null) as MeterAppointmentStatus | null,
            item.appointment_type ?? "",
            item.meter_type ?? "",
            item.installation_type ?? "",
            item.timeslot ?? "",
            item.updated_at ? dayjs(item.updated_at) : dayjs(item.created_at),
            safeDayjs(item.cancellation_date_time),
            safeDayjs(item.installed_datetime),
            safeDayjs(item.failed_datetime),
            toSurvey(item.survey),
            toHistory(item.history),
            toEvents(item.events),
            toComments(item.comments),
            item.abort_reason ?? undefined,
            item.cancel_reason ?? undefined,
            item.mop ?? undefined,
          ),
      )
    } catch (error) {
      throw new ParseError(error)
    }
  },
}
